import "./App.css";
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BrowserRouter as Router } from "react-router-dom";
import axios from "axios";
import MainLayout from "./components/Layout/MainLayout";
import AppRoutes from "./components/Layout/AppRoutes";
import { useChimera } from "./chimera/ChimeraContext";

const WarningBanner = () => {
  const bannerText = process.env.REACT_APP_BANNER_TEXT;
  
  if (!bannerText) {
    return null;
  }

  return (
    <div className="bg-yellow-500 text-yellow-900 px-4 py-3 flex items-center justify-center gap-2">
      <svg 
        className="w-5 h-5 flex-shrink-0" 
        fill="currentColor" 
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path 
          fillRule="evenodd" 
          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" 
          clipRule="evenodd" 
        />
      </svg>
      <span className="font-medium text-sm">{bannerText}</span>
    </div>
  );
};

function App({ chimeraProvider }) {
  const queryClient = useQueryClient();
  const { isConnected } = useChimera();

  const [userLoggedIn, setUserLoggedIn] = React.useState(null);
  const [isSubUser, setIsSubUser] = React.useState(null);
  const [showLoading, setShowLoading] = React.useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);

  const { status, data, error, isFetching } = useQuery(["user"], () => {
    return axios.get(`${process.env.REACT_APP_API_ROOT}/me`, {
      headers: {
        Accept: "application/json",
      },
    });
  });

  const inbox = useQuery(
    ["inbox"],
    () => {
      return axios.get(`${process.env.REACT_APP_API_ROOT}/inbox`, {
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      enabled: !isConnected,
    }
  );

  const myusers = useQuery(
    ["myusers"],
    () => {
      return axios.get(`${process.env.REACT_APP_API_ROOT}/myusers`, {
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      enabled: !isConnected,
    }
  );

  const myapps = useQuery(
    ["myapps"],
    () => {
      return axios.get(`${process.env.REACT_APP_API_ROOT}/myapps`, {
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      enabled: !isConnected,
    }
  );

  const myusersapps = useQuery(
    ["myusersapps"],
    () => {
      return axios.get(`${process.env.REACT_APP_API_ROOT}/myusersapps`, {
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      enabled: !isConnected,
    }
  );

  const myarchive = useQuery(
    ["myarchive"],
    () => {
      return axios.get(`${process.env.REACT_APP_API_ROOT}/myarchive`, {
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      enabled: !isConnected,
    }
  );

  useEffect(() => {
    if (status === "success" && data && data.data) {
      setUserLoggedIn(data.data);
      if (data.data.trialExpiry) {
        if (new Date() > new Date(data.data.trialExpiry)) {
          setErrorDialogOpen(true);
        }
      }
    } else if (status !== "success" && data && data.data) {
      localStorage.removeItem("tokenmob");
    }
  }, [status, data]);

  const doLogOut = () => {
    localStorage.removeItem("tokenmob");
    document.location.href = "/login";
  };

  // Iframe mode
  if (isConnected) {
    return (
      <Router>
        <WarningBanner />
        <main className="bg-gray-100 min-h-screen p-6">
          <AppRoutes
            userLoggedIn={userLoggedIn}
            setShowLoading={setShowLoading}
            queryClient={queryClient}
            isConnected={isConnected}
          />
        </main>
      </Router>
    );
  }

  return (
    <Router>
      <MainLayout
        userLoggedIn={userLoggedIn}
        myAppsCount={myapps?.data?.data?.length || 0}
        inboxCount={inbox?.data?.data?.length || 0}
        myUsersCount={myusers?.data?.data?.length || 0}
        myArchiveCount={myarchive.data?.data?.length || 0}
        setShowLoading={setShowLoading}
        showLoading={showLoading}
        errorDialogOpen={errorDialogOpen}
        doLogOut={doLogOut}
        queryClient={queryClient}
        isConnected={isConnected}
      />
    </Router>
  );
}

export default App;
